<script lang="ts" setup>
import type { CarImageSize } from '@autobid/ui/types/Car'
import type { DetailsTable } from '@autobid/ui/types/components/CarDetails'
import { useCarGallery } from '@autobid/ui/composables/car/useCarGallery'
// ignore the warning - it's used directly in the template
// eslint-disable-next-line unused-imports/no-unused-imports
import { replaceImageSize } from '@autobid/ui/utils/car/generateImageSizes'
import PdfTableWrapper from '../pdf/elements/TableWrapper.vue'
import AppTableWrapper from './TableWrapper.vue'

interface Props {
  table: DetailsTable
}

defineProps<Props>()

const isPdfPage = inject('isPdfPage', false)
const { injectShowGallery, openCarGallery } = useCarGallery()
const showGallery = injectShowGallery()

function handleClickImage(links: Record<CarImageSize, string>) {
  showGallery.value = true
  openCarGallery(links)
}
</script>

<template>
  <component :is="isPdfPage ? PdfTableWrapper : AppTableWrapper">
    <table class="w-full border-collapse">
      <thead v-if="table.tableHeadings?.length">
        <tr class="text-left text-primary">
          <th
            v-for="cell in table.tableHeadings"
            :key="cell"
            class="px-1 pb-3"
            :class="{ 'font-normal': isPdfPage }"
          >
            {{ cell }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in table.tableCells"
          :key="index"
          :class="{
            'border-t': index !== 0 || table.tableHeadings?.length,
          }"
          class="border-[#c1c1c1]"
          :style="
            isPdfPage && (index !== 0 || table.tableHeadings?.length)
              ? 'border-top: 1px solid #c1c1c1'
              : undefined
          "
        >
          <td
            v-for="cell in row"
            :key="cell?.toString()"
            class="px-1 align-top"
            :class="{
              'py-4': !(
                cell
                && typeof cell === 'object'
                && 'imageType' in cell
              ),
              'td-image text-center':
                cell && typeof cell === 'object' && 'imageType' in cell,
              'align-middle': typeof cell === 'boolean' || cell === null,
            }"
          >
            <span v-if="typeof cell === 'string'">{{ cell }}</span>

            <table
              v-else-if="typeof cell === 'boolean' || cell === null"
              class="h-2.5 w-10 rounded-full"
              :class="{
                'my-1.5': !isPdfPage,
              }"
            >
              <tr>
                <td
                  class="td-pill h-2.5 w-10 rounded-full align-middle"
                  :class="{
                    'bg-primary': cell,
                  }"
                  :style="isPdfPage ? 'font-size:7px' : undefined"
                >
                  <span v-if="isPdfPage">&nbsp;</span>
                </td>
              </tr>
            </table>

            <ul v-else-if="Array.isArray(cell)" class="my-0 py-0">
              <li v-for="text in cell" :key="text">
                <SectionsDetailsElementsText
                  v-if="Array.isArray(text)"
                  :label="text[0]"
                  :text="text[1]"
                />
                <SectionsDetailsElementsText v-else :text="text" />
              </li>
            </ul>

            <template
              v-else-if="typeof cell === 'object' && 'imageType' in cell"
            >
              <component
                :is="isPdfPage ? 'span' : 'button'"
                v-if="'imageType' in cell"
                class="relative z-10 mt-1 inline-block"
                :aria-label="$t('car-details.open-gallery')"
                @click="handleClickImage(cell.links)"
              >
                <img
                  :="
                    isPdfPage
                      ? {
                          'data-src': replaceImageSize(
                            cell.links.hd,
                            'hd',
                            'xs'
                          )
                        }
                      : {
                          src: replaceImageSize(cell.links.hd, 'hd', 'xs'),
                          loading: 'lazy'
                        }
                  "
                  :alt="cell.description.categoryLabel"
                  class="relative z-10 h-12"
                >
                <SectionsDetailsElementsImageHoverOverlay
                  v-if="!isPdfPage"
                  size="small"
                />
              </component>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </component>
</template>
